import { useContext } from 'react';

import {
  assertCartContext,
  CartActionsContext,
  CartStateContext,
  CartSummaryContext,
  type CartActions,
} from './cart.context';
import type { CartState, CartSummary } from './cart.types';

/**
 * Actions only. Stable for the lifetime of the app, so consumers of this hook
 * never re-render because of a cart change — use it in product cards.
 */
export function useCartActions(): CartActions {
  return assertCartContext(useContext(CartActionsContext), 'useCartActions');
}

/** Raw state, including whether the drawer is open. */
export function useCartState(): CartState {
  return assertCartContext(useContext(CartStateContext), 'useCartState');
}

/** Resolved lines and money totals. Re-renders whenever the cart changes. */
export function useCartSummary(): CartSummary {
  return assertCartContext(useContext(CartSummaryContext), 'useCartSummary');
}

/** How many units of a SKU are already in the cart. */
export function useCartQuantity(sku: string): number {
  const { lines } = useCartSummary();
  return lines.find((line) => line.sku === sku)?.quantity ?? 0;
}
